/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLeading } from 'redux-saga/effects';
import {
  ActionRequest,
  ActionSuccess,
  Asset,
  ActionFail,
  XError,
  CollectionEntitlement,
  SearchUtils,
  FileFormatToFileTypeMap,
  Folder,
  Constants,
  AssetsApi,
  AssetMediaType,
  PpvEntitlement,
  TimeUtils,
  isPpvEntitlement,
  isCollectionEntitlement,
  HideUnsubscribed,
  ParentalControlSettings,
  Ducks,
  AssetUtils,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import { Selectors as PpvEntitlementsSelectors } from '../ppv';
import { Selectors as CollectionEntitlementsSelectors } from '../collection';
import AppConfig from '../../../../../services/AppConfig';

export enum ActionType {
  REQUEST = 'entitlements/assets/entitledAssets/REQUEST',
  SUCCESS = 'entitlements/assets/entitledAssets/SUCCESS',
  FAIL = 'entitlements/assets/entitledAssets/FAIL',
}

type Request = ActionRequest<ActionType.REQUEST>;
type Success = ActionSuccess<ActionType.SUCCESS, Asset[]>;
type Fail = ActionFail<ActionType.FAIL>;

export type Actions = Request | Success | Fail;

export interface ReducerState {
  data: Asset[];
  processing?: boolean;
  processed?: boolean;
  error?: XError;
}

const Actions = {
  request: (): Request => ({
    type: ActionType.REQUEST,
  }),
  success: (assets: Asset[]): Success => ({
    type: ActionType.SUCCESS,
    payload: assets,
  }),
  fail: (error: XError): Fail => ({
    type: ActionType.FAIL,
    error,
  }),
};

export const ActionCreators = {
  getEntitlementsAssets: Actions.request,
};

const initialState: ReducerState = {
  data: [],
  processing: false,
  processed: false,
};

export default function myHome(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.REQUEST:
      return {
        ...state,
        processing: true,
        processed: false,
        error: undefined, // reset error
      };
    case ActionType.SUCCESS:
      return {
        data: action.payload,
        processing: false,
        processed: true,
      };
    case ActionType.FAIL:
      return {
        data: initialState.data,
        processing: false,
        processed: true,
        error: action.error,
      };
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  isProcessing: (state: StoreState): boolean | undefined => state.entitlements.assets.entitledAssets.processing,
  isProcessed: (state: StoreState): boolean | undefined => state.entitlements.assets.entitledAssets.processed,
  getAssets: (state: StoreState): Asset[] => state.entitlements.assets.entitledAssets.data,
};

export const PAGINATED_ENTITLEMENTS_COLLECTION_NAMESPACE = 'ENTITLEMENTS_ASSETS_COLLECTION';

function* getCollectionAssetSaga(): SagaIterator {
  const collectionEntitlemets: CollectionEntitlement[] = yield select(
    CollectionEntitlementsSelectors.getCollectionEntitlements
  ) || [];

  if (collectionEntitlemets.length > 0) {
    const collectionEntitlementsIds = collectionEntitlemets.map((e) => e.id);
    const ksql = SearchUtils.createFoldersFilter(collectionEntitlementsIds, 'collectionId');

    const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
    const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

    const result: Folder[] = yield Ducks.Pagination.callCompletedPaginatedApi(
      // TODO: Every time the entitlements are refresh, the namespace for the ppv and collections should be invalidated
      PAGINATED_ENTITLEMENTS_COLLECTION_NAMESPACE,
      Constants.DEFAULT_API_PAGE_SIZE,
      AssetsApi.assetSearch,
      ksql,
      filesFormat,
      parentalControlSettings,
      [AssetMediaType.FOLDER],
      undefined,
      HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY]
    );

    return result;
  }

  return [];
}

export const PAGINATED_ENTITLEMENTS_PPV_NAMESPACE = 'ENTITLEMENTS_ASSETS_PPV';

function* getPpvAssetSaga(): SagaIterator {
  const ppvEntitlemets: PpvEntitlement[] = (yield select(PpvEntitlementsSelectors.getPpvEntitlements)) || [];

  if (ppvEntitlemets.length > 0) {
    const ppvEntitlementsIds = ppvEntitlemets.map((e) => e.mediaId);
    const ksql = SearchUtils.createPurchasesAndRentalsFilter(ppvEntitlementsIds);
    const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
    const mediaTypes = [AssetMediaType.MOVIE, AssetMediaType.EPISODE];
    const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

    const result: Asset[] = yield Ducks.Pagination.callCompletedPaginatedApi(
      // TODO: Every time the entitlements are refresh, the namespace for the ppv and collections should be invalidated
      PAGINATED_ENTITLEMENTS_PPV_NAMESPACE,
      Constants.DEFAULT_API_PAGE_SIZE,
      AssetsApi.assetSearch,
      ksql,
      filesFormat,
      parentalControlSettings,
      mediaTypes,
      undefined,
      HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY]
    );

    return result;
  }

  return [];
}

function* getAssetsSaga(): SagaIterator {
  try {
    const collectionEntitlemets: CollectionEntitlement[] = yield select(
      CollectionEntitlementsSelectors.getCollectionEntitlements
    ) || [];
    const ppvEntitlemets: PpvEntitlement[] = (yield select(PpvEntitlementsSelectors.getPpvEntitlements)) || [];

    const sortedEntitlemets = [...collectionEntitlemets, ...ppvEntitlemets].sort((a, b) => {
      return TimeUtils.compare(b.purchaseDate, a.purchaseDate);
    });

    const idAssetMap = new Map<string, Asset>();
    const sortedEntitlementsAssets: Asset[] = [];

    const [collectionAssets, ppvAssets]: Asset[][] = yield all([call(getCollectionAssetSaga), call(getPpvAssetSaga)]);

    [...collectionAssets, ...ppvAssets].forEach((asset) => {
      if (AssetUtils.isEpisode(asset) || AssetUtils.isMovie(asset)) {
        idAssetMap.set(`P:${asset.id}`, asset);
      } else if (AssetUtils.isFolder(asset) && asset.productId) {
        idAssetMap.set(`C:${asset.productId}`, asset);
      }
    });

    sortedEntitlemets.forEach((entitlement) => {
      let id;
      if (isPpvEntitlement(entitlement)) {
        id = `P:${entitlement.mediaId}`;
      } else if (isCollectionEntitlement(entitlement)) {
        id = `C:${entitlement.id}`;
      }

      if (id) {
        const asset = idAssetMap.get(id);
        if (asset) {
          sortedEntitlementsAssets.push(asset);
          idAssetMap.delete(id);
        }
      }
    });

    yield put(Actions.success(sortedEntitlementsAssets));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeLeading(ActionType.REQUEST, getAssetsSaga)]);
}
